// Multi-camera investigation timeline.
//
// One lane per selected camera (sticky 132px name gutter + scrollable track).
// Reads everything it needs through props — no API calls here. Mutations
// flow back through the callback props (`onClickEvent`, `onSelectRange`,
// `onSwitchCamera`, `onToggleGrid`, `onScrub`).
//
// The wheel handler routes vertical scroll over the lane gutter to native
// scroll (so a tall list scrolls naturally) and zooms when the cursor is
// over a track (NLE convention).

import { useCallback, useEffect, useRef } from "react";

import type { CameraEvent } from "@/api/eventsClient";

import {
  KIND_ORDER,
  KINDS,
  visualKindFor,
  type EventKindId,
} from "../helpers/kinds";
import { formatTick, tOf, xOf, type AbsoluteWindow } from "../helpers/window";
import type { PlayableSegment } from "../helpers/segments";
import type { ScopeCamera } from "../hooks/useScopeSelection";

import { RangeControl } from "./RangeControl";
import type { TimelineWindowApi } from "../hooks/useTimelineWindow";

const LANE_NAME_W = 132;
const TICKS = 6;

export interface TimelineProps {
  /** Selected cameras, in lane order. */
  cameras: readonly ScopeCamera[];
  /** Per-camera event lists; keyed by doorbot_id. */
  eventsByCamera: ReadonlyMap<string, readonly CameraEvent[]>;
  /** True iff the surface is in grid view (changes lane affordances). */
  gridMode: boolean;
  /** Cameras currently in the grid (subset of `cameras`). */
  gridCameras: readonly string[];
  /** doorbot_id of the camera whose clip is currently loaded in single view (or null). */
  loadedCameraId: string | null;
  /** Loaded clip range across the timeline (single mode) or grid window. */
  loadedRange: { startMs: number; durMs: number } | null;
  /** Loaded clip's segments (paint per-lane coverage cells on the loaded camera's lane). */
  loadedSegments: readonly PlayableSegment[];
  /** Wall-clock anchor of the live edge (NOW). */
  nowMs: number;
  /** Wall-clock instant before which the consent wall hatches the lane. */
  consentAtMs: number;
  /** Window control. */
  window: TimelineWindowApi;
  /** Selected event id (for visual highlight). */
  selectedEventId: string | null;
  /** Filter chip state. */
  kindFilter: ReadonlySet<EventKindId>;
  personOnly: boolean;
  /** Currently-selected (highlighted) event for the engine. */
  playheadFrac: number | null;

  onToggleKind(k: EventKindId): void;
  onTogglePersonOnly(): void;
  onClickEvent(ev: CameraEvent, cam: ScopeCamera): void;
  onSelectRange(cam: ScopeCamera, t1: number, t2: number): void;
  onSwitchCamera(id: string): void;
  onToggleGrid(id: string): void;
  /**
   * Called when the user grabs the playhead and drags. Receives the wall-
   * clock instant and a "still dragging" flag so the parent can pause/play.
   */
  onScrub(wallMs: number, dragging: boolean): void;
}

interface BrushState {
  trackEl: HTMLElement;
  cam: ScopeCamera;
  startX: number;
  el: HTMLElement;
}

export function Timeline(props: TimelineProps): JSX.Element {
  const {
    cameras,
    eventsByCamera,
    gridMode,
    gridCameras,
    loadedCameraId,
    loadedRange,
    loadedSegments,
    nowMs,
    consentAtMs,
    window: w,
    selectedEventId,
    kindFilter,
    personOnly,
    playheadFrac,
    onToggleKind,
    onTogglePersonOnly,
    onClickEvent,
    onSelectRange,
    onSwitchCamera,
    onToggleGrid,
    onScrub,
  } = props;

  const lanesRef = useRef<HTMLDivElement | null>(null);
  const playheadRef = useRef<HTMLDivElement | null>(null);
  const brushRef = useRef<BrushState | null>(null);
  const draggingRef = useRef(false);

  const wallFrac = Math.max(0, xOf(consentAtMs, w.win));

  /**
   * Lanes are sorted: in grid mode, in-grid cameras to the top in grid order;
   * the rest in selection order. In single mode just the selection order.
   */
  const sortedCameras: readonly ScopeCamera[] = (() => {
    if (!gridMode) return cameras;
    const order = new Map(gridCameras.map((id, i) => [id, i]));
    return [...cameras].sort((a, b) => {
      const ai = order.has(a.doorbot_id) ? order.get(a.doorbot_id)! : 1e9;
      const bi = order.has(b.doorbot_id) ? order.get(b.doorbot_id)! : 1e9;
      return ai - bi;
    });
  })();

  // ---------------------------------------------------------------- scrub
  const beginScrub = useCallback(
    (e: React.MouseEvent) => {
      if (cameras.length === 0) return;
      e.preventDefault();
      const lanesEl = lanesRef.current;
      if (!lanesEl) return;
      const trackEl = lanesEl.querySelector<HTMLElement>(".rec-track");
      if (!trackEl) return;
      draggingRef.current = true;
      const phEl = playheadRef.current;
      if (phEl) phEl.classList.add("dragging");
      lanesEl.classList.add("scrubbing");

      const onMove = (ev: MouseEvent): void => {
        const r = trackEl.getBoundingClientRect();
        const f = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width));
        const wallMs = tOf(f, w.win);
        if (phEl) {
          phEl.style.left = `calc(${LANE_NAME_W}px + (100% - ${LANE_NAME_W}px) * ${f})`;
        }
        onScrub(wallMs, true);
      };
      const onUp = (ev: MouseEvent): void => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        draggingRef.current = false;
        if (phEl) phEl.classList.remove("dragging");
        lanesEl.classList.remove("scrubbing");
        const r = trackEl.getBoundingClientRect();
        const f = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width));
        onScrub(tOf(f, w.win), false);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      onMove(e.nativeEvent);
    },
    [cameras.length, onScrub, w.win],
  );

  // ---------------------------------------------------------------- brush
  const beginBrush = useCallback(
    (e: React.MouseEvent, cam: ScopeCamera) => {
      if ((e.target as HTMLElement).closest(".rec-ev")) return;
      const trackEl = e.currentTarget as HTMLElement;
      const r = trackEl.getBoundingClientRect();
      const startX = e.clientX - r.left;
      const el = document.createElement("div");
      el.className = "rec-brush";
      el.style.left = `${startX}px`;
      el.style.width = "0";
      trackEl.appendChild(el);
      brushRef.current = { trackEl, cam, startX, el };

      const onMove = (ev: MouseEvent): void => {
        const x = Math.max(0, Math.min(r.width, ev.clientX - r.left));
        el.style.left = `${Math.min(startX, x)}px`;
        el.style.width = `${Math.abs(x - startX)}px`;
      };
      const onUp = (ev: MouseEvent): void => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        const x = Math.max(0, Math.min(r.width, ev.clientX - r.left));
        const f1 = Math.min(startX, x) / r.width;
        const f2 = Math.max(startX, x) / r.width;
        const t1 = Math.floor(tOf(f1, w.win));
        const t2 = Math.floor(tOf(f2, w.win));
        // Reject ranges shorter than ~20s — the user just clicked.
        if (Math.abs(t2 - t1) < 20_000) {
          el.remove();
          brushRef.current = null;
          return;
        }
        // Drop any other brushes from the page first.
        for (const other of trackEl.parentElement?.querySelectorAll(".rec-brush") ?? []) {
          if (other !== el) other.remove();
        }
        brushRef.current = null;
        onSelectRange(cam, t1, t2);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [onSelectRange, w.win],
  );

  // ---------------------------------------------------------------- wheel
  useEffect(() => {
    const lanesEl = lanesRef.current;
    if (!lanesEl) return;
    const onWheel = (e: WheelEvent): void => {
      const trackEl = lanesEl.querySelector<HTMLElement>(".rec-track");
      if (!trackEl) return;
      const r = trackEl.getBoundingClientRect();
      const overTrack = e.clientX >= r.left;
      if (!overTrack) return; // gutter -> native vertical scroll
      if (Math.abs(e.deltaY) < 2) return;
      e.preventDefault();
      const a = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
      w.zoom(e.deltaY > 0 ? 1.18 : 1 / 1.18, a);
    };
    lanesEl.addEventListener("wheel", onWheel, { passive: false });
    return () => lanesEl.removeEventListener("wheel", onWheel);
  }, [w]);

  // ---------------------------------------------------------------- ticks
  const ticks: { label: string; key: number }[] = [];
  for (let i = 0; i < TICKS; i++) {
    ticks.push({
      key: i,
      label: formatTick(tOf(i / TICKS, w.win), w.win.end - w.win.start),
    });
  }

  const nowFx = xOf(nowMs, w.win);
  const showLiveEdge = nowFx >= 0 && nowFx <= 1;

  return (
    <div className="rec-tlwrap">
      <div className="rec-tl-h">
        <span className="rec-tl-t">Timeline</span>
        <div className="rec-chips" role="group" aria-label="Filter event types">
          {KIND_ORDER.map((k) => {
            const active = kindFilter.has(k);
            return (
              <button
                key={k}
                type="button"
                className={`rec-chip${active ? " active" : " off"}`}
                onClick={() => onToggleKind(k)}
                aria-pressed={active}
              >
                <span className={`gl ${KINDS[k].cls}`} aria-hidden="true" />
                {KINDS[k].label}
              </button>
            );
          })}
        </div>
        <label className="rec-tl-toggle">
          <input
            type="checkbox"
            checked={personOnly}
            onChange={onTogglePersonOnly}
          />
          Person only
        </label>
        <RangeControl window={w} />
      </div>
      <div className="rec-axis" role="presentation">
        {ticks.map((t) => (
          <div className="rec-tick" key={t.key}>
            {t.label}
          </div>
        ))}
      </div>
      {loadedSegments.length > 0 && (
        <RenderBar segments={loadedSegments} win={w.win} />
      )}
      <div
        ref={lanesRef}
        className={`rec-lanes${gridMode ? " grid-mode" : ""}`}
      >
        <CliprangeBand range={loadedRange} win={w.win} />
        {sortedCameras.length === 0 && (
          <div
            className="rec-lane"
            style={{ height: 140, display: "grid", placeItems: "center" }}
          >
            <span style={{ color: "#56657a", fontSize: 12 }}>
              No cameras selected — pick a location or cameras on the right.
            </span>
          </div>
        )}
        {sortedCameras.map((cam) => {
          const active = gridMode && gridCameras.includes(cam.doorbot_id);
          const loaded = !gridMode && loadedCameraId === cam.doorbot_id;
          const gi = active ? gridCameras.indexOf(cam.doorbot_id) + 1 : 0;
          const evs = eventsByCamera.get(cam.doorbot_id) ?? [];
          return (
            <div
              key={cam.doorbot_id}
              className={`rec-lane${active ? " grid-on" : ""}${loaded ? " cam-loaded" : ""}`}
            >
              <div
                className="rec-lane-name toggleable"
                title={
                  gridMode
                    ? active
                      ? "Click to remove from grid"
                      : "Click to add to grid"
                    : loaded
                      ? "Now showing this camera"
                      : "Click to switch to this camera"
                }
                role="button"
                tabIndex={0}
                onClick={() => (gridMode ? onToggleGrid(cam.doorbot_id) : onSwitchCamera(cam.doorbot_id))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (gridMode) onToggleGrid(cam.doorbot_id);
                    else onSwitchCamera(cam.doorbot_id);
                  }
                }}
              >
                {gridMode ? (
                  <button
                    type="button"
                    className="rec-lane-toggle"
                    title={active ? "Remove from grid" : "Add to grid"}
                    aria-pressed={active}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleGrid(cam.doorbot_id);
                    }}
                  >
                    {active ? (
                      <span className="rec-gi">{gi}</span>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    )}
                  </button>
                ) : (
                  <span className="rec-lane-cam-ic" aria-hidden="true">
                    {loaded ? (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 7a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7Z" />
                        <path d="m15 10 6-3v10l-6-3" />
                      </svg>
                    )}
                  </span>
                )}
                <span className={`rec-st${cam.online ? "" : " off"}`} aria-hidden="true" />
                <span className="rec-nm">{cam.name}</span>
              </div>
              <div
                className="rec-track"
                onMouseDown={(e) => beginBrush(e, cam)}
              >
                <div className={`rec-cov${cam.has_real ? " real" : ""}`} />
                {wallFrac > 0 && (
                  <div
                    className="rec-wall"
                    style={{ width: `${Math.min(wallFrac, 1) * 100}%` }}
                  >
                    {wallFrac > 0.05 && (
                      <span className="rec-wall-lab">no access</span>
                    )}
                  </div>
                )}
                {loaded &&
                  loadedSegments.map((seg) => {
                    const a = xOf(seg.start_ms, w.win);
                    const b = xOf(seg.start_ms + seg.duration_ms, w.win);
                    if (b < 0 || a > 1) return null;
                    const L = Math.max(0, a);
                    const R = Math.min(1, b);
                    return (
                      <div
                        key={seg.index}
                        className={`rec-covseg ${seg.state}`}
                        style={{
                          left: `${L * 100}%`,
                          width: `${(R - L) * 100}%`,
                        }}
                      />
                    );
                  })}
                {evs.map((ev) => {
                  const kind = visualKindFor(ev);
                  if (kind === null) return null;
                  if (!kindFilter.has(kind)) return null;
                  if (personOnly && !ev.person_detected) return null;
                  const fx = xOf(ev.start_ms, w.win);
                  if (fx < 0 || fx > 1) return null;
                  const sel = selectedEventId === ev.ding_id;
                  return (
                    <button
                      key={ev.ding_id}
                      type="button"
                      className={`rec-ev${sel ? " sel" : ""}`}
                      style={{ left: `${fx * 100}%` }}
                      title={`${KINDS[kind].label} · ${new Date(ev.start_ms).toLocaleString()} · ${ev.duration_seconds}s`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickEvent(ev, cam);
                      }}
                      aria-label={`${KINDS[kind].label} ${new Date(ev.start_ms).toLocaleString()} on ${cam.name}`}
                    >
                      <span className={`gl ${KINDS[kind].cls}`} aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {showLiveEdge && (
          <div
            className="rec-liveedge"
            style={{
              left: `calc(${LANE_NAME_W}px + (100% - ${LANE_NAME_W}px) * ${nowFx})`,
            }}
            aria-hidden="true"
          >
            <span className="rec-lab">live edge · now</span>
          </div>
        )}
        {playheadFrac !== null && (
          <div
            ref={playheadRef}
            className="rec-playhead"
            style={{
              left: `calc(${LANE_NAME_W}px + (100% - ${LANE_NAME_W}px) * ${playheadFrac})`,
            }}
            onMouseDown={beginScrub}
            role="slider"
            aria-orientation="horizontal"
            aria-label="Scrubber"
            aria-valuemin={w.win.start}
            aria-valuemax={w.win.end}
            aria-valuenow={tOf(playheadFrac, w.win)}
            tabIndex={0}
          >
            <div className="rec-hit" aria-hidden="true" />
            <div className="rec-grip" aria-hidden="true" />
          </div>
        )}
      </div>
      <div className="rec-tl-foot">
        <span>
          Drag the <span style={{ color: "#fff" }}>● scrubber</span> to seek · click a
          glyph or drag a lane to load · the <b style={{ color: "#7fd4a0" }}>Stitched</b> bar shows segments{" "}
          <span style={{ color: "#7fd4a0" }}>loaded</span> /{" "}
          <span style={{ color: "#42B4FF" }}>in-flight</span> / queued · before the{" "}
          <span style={{ color: "#ff8f7c" }}>consent wall</span> → <code>403</code>
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- subcomponents

interface RenderBarProps {
  segments: readonly PlayableSegment[];
  win: AbsoluteWindow;
}

function RenderBar({ segments, win }: RenderBarProps): JSX.Element {
  return (
    <div className="rec-renderbar">
      <span className="rec-rlabel">Stitched</span>
      {segments.map((seg) => {
        const a = xOf(seg.start_ms, win);
        const b = xOf(seg.start_ms + seg.duration_ms, win);
        if (b < 0 || a > 1) return null;
        const L = Math.max(0, a);
        const R = Math.min(1, b);
        return (
          <div
            key={seg.index}
            className={`rec-rseg ${seg.state}`}
            style={{
              left: `calc(100% * ${L})`,
              width: `calc(100% * ${Math.max(0.004, R - L)})`,
            }}
          />
        );
      })}
    </div>
  );
}

interface CliprangeBandProps {
  range: { startMs: number; durMs: number } | null;
  win: AbsoluteWindow;
}

function CliprangeBand({ range, win }: CliprangeBandProps): JSX.Element | null {
  if (!range) return null;
  const a = xOf(range.startMs, win);
  const b = xOf(range.startMs + range.durMs, win);
  if (b < 0 || a > 1) return null;
  const L = Math.max(0, a);
  const R = Math.min(1, b);
  return (
    <div
      className="rec-cliprange"
      style={{
        left: `calc(${LANE_NAME_W}px + (100% - ${LANE_NAME_W}px) * ${L})`,
        width: `calc((100% - ${LANE_NAME_W}px) * ${R - L})`,
      }}
      aria-hidden="true"
    />
  );
}
